import { evaluate, MISSING_PROPERTY_VALUE, } from "@msys/document-expressions/evaluate";
/**
 * The question's props visible for the staged values, in prop order: a prop
 * whose ask-if is in the context is visible iff it evaluates to `true` with the
 * server's engine; every other prop keeps the server's visibility.
 *
 * Hiding a prop never drops its stage: every answered stage is submitted, so
 * the ask-ifs are evaluated once over all of them, as the server does.
 */
export function visiblePropKeys(args) {
    const { itemId, props, askIf, staged } = args;
    const serverVisible = new Set(askIf.visiblePropKeys);
    const { expressions, inputs } = askIf.context;
    // An unanswered stage is not submitted, so the server keeps the prop's
    // current value: only answered stages override the inputs.
    const answered = staged.filter(entry => hasStagedValue(entry.value));
    const answeredKeys = new Set(answered.map(entry => entry.key));
    const properties = [
        ...inputs
            .filter(input => input.scope === "item" &&
            !(input.itemId === itemId && answeredKeys.has(input.key)))
            .map(({ itemId, key, value }) => ({ itemId, key, value })),
        ...answered.map(entry => ({
            itemId,
            key: entry.key,
            value: stagedToPropertyValue(entry),
        })),
    ];
    const productProperties = inputs
        .filter(input => input.scope === "product")
        .map(({ itemId, key, value }) => ({ itemId, key, value }));
    const outcomes = evaluateOrUndefined(expressions.map((expression, index) => ({
        id: String(index),
        ...expression,
    })), { properties, productProperties });
    // The stages fail the server's recompute too; the page keeps the server's
    // visibility meanwhile.
    if (!outcomes) {
        return props.map(prop => prop.key).filter(key => serverVisible.has(key));
    }
    const askIfOutcomes = new Map(expressions.flatMap((expression, index) => expression.target.kind === "ITEM_PROPERTY_ASK_IF" &&
        expression.target.itemId === itemId
        ? [[expression.target.propertyKey, outcomes[index] ?? null]]
        : []));
    return props
        .map(prop => prop.key)
        .filter(key => {
        if (!askIfOutcomes.has(key))
            return serverVisible.has(key);
        const outcome = askIfOutcomes.get(key);
        return outcome?.kind === "boolean" && outcome.value === true;
    });
}
function evaluateOrUndefined(expressions, input) {
    try {
        return evaluate(expressions, input);
    }
    catch {
        return undefined;
    }
}
/**
 * Whether a change answers a single-property page on its own: exactly one prop
 * is visible after the change, it is the changed one, picking it is a single
 * click, and it now holds a value.
 */
export function autoSubmits(args) {
    const { visibleAfterChange, changed } = args;
    return (visibleAfterChange.length === 1 &&
        visibleAfterChange[0] === changed.key &&
        changed.pickable &&
        hasStagedValue(changed.value));
}
/** An empty text or selection is unanswered; `0` and `false` are answers. */
export function hasStagedValue(value) {
    if (value === null || value === undefined)
        return false;
    if (typeof value === "string" || Array.isArray(value)) {
        return value.length > 0;
    }
    return true;
}
function stagedToPropertyValue(entry) {
    if (entry.value === null)
        return MISSING_PROPERTY_VALUE;
    switch (entry.kind) {
        case "Props2Bool":
            return { kind: "boolean", value: entry.value };
        case "Props2Number":
            return { kind: "number", value: entry.value };
        case "Props2Text":
            return { kind: "text", value: entry.value };
        case "Props2NumberArray":
            return { kind: "number_array", value: entry.value };
        case "Props2TextArray":
            return { kind: "text_array", value: entry.value };
    }
}
//# sourceMappingURL=askIf.js.map